import { Banner, Button, Dialog, TextLink, Typography } from '@neo4j-ndl/react';
import { CreditCardIconOutline } from '@neo4j-ndl/react/icons';
import type { Instance, Project } from '@nx/state';
import { CLOUD_PROVIDER, INSTANCE_STATUS, MODAL_TYPE, consoleApi, useInstances, useModal } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { useDarkMode } from '@nx/ui';
import classNames from 'classnames';
import * as React from 'react';
import { type ReactNode, useMemo, useState } from 'react';

import AWSDark from '../../../assets/aws-dark.svg';
import AWS from '../../../assets/aws.svg';
import Azure from '../../../assets/azure.svg';
import GCP from '../../../assets/gcp.svg';
import { findInstanceSize } from '../../../instance/entities/helpers';
import { StripeFormFallback } from '../../../loading-fallbacks';
import { formatCost, getCostMonth, getCurrencyForBillingMethod } from '../../../utils';
import './stripe-overrides.css';

const StripeForm = React.lazy(() => import('./stripe-form'));

interface Props {
  onClose: () => void;
  project: Project;
}

function formatDate(date: string) {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  return new Date(date).toLocaleDateString('en-US', options);
}

const PAUSED_STATUSES = [INSTANCE_STATUS.PAUSED, INSTANCE_STATUS.PAUSING];

type MessageBannerProps = { instance: Instance } & Props;

export function MessageBanner({ project, instance, onClose }: MessageBannerProps) {
  const tierConfig = project.tierConfigs[instance.tier];
  const size = findInstanceSize(instance, tierConfig?.sizes ?? []);
  const configureModal = useModal(MODAL_TYPE.CONFIGURE);

  if (isNullish(tierConfig) || isNullish(size) || isNullish(instance.trialEndTime)) {
    return <></>;
  }

  const resizeInstance = () => {
    onClose();
    configureModal.open({ instance, project });
  };

  const costPerHour = formatCost(size.costPerHour, getCurrencyForBillingMethod(project.billingMethod));
  const costPerMonth = formatCost(getCostMonth(size.costPerHour), getCurrencyForBillingMethod(project.billingMethod));

  if (PAUSED_STATUSES.includes(instance.instanceStatus)) {
    return (
      <Typography variant="body-medium" className="flex flex-col gap-2" as="div">
        <Banner className="my-4" hasIcon>
          Based on your paused trial instance, you will be charged <span className="font-bold">{costPerHour}/hour</span>{' '}
          (approximately {costPerMonth}/month) <span className="font-bold">after the trial expires</span> on{' '}
          {formatDate(instance.trialEndTime)}.
        </Banner>
      </Typography>
    );
  }

  return (
    <Typography variant="body-medium" className="flex flex-col gap-2" as="div">
      <Banner className="mb-4" hasIcon>
        Based on the size of your trial instance, you will be charged{' '}
        <span className="font-bold">{costPerHour}/hour</span> (approximately {costPerMonth}
        /month) <span className="font-bold">after the trial expires</span> on {formatDate(instance.trialEndTime)}. You
        can <TextLink htmlAttributes={{ onClick: resizeInstance }}>edit the size</TextLink> of your trial instance to
        adjust the cost.
      </Banner>
    </Typography>
  );
}

const IGNORABLE_STATUSES = [INSTANCE_STATUS.DESTROYED, INSTANCE_STATUS.DESTROYING, INSTANCE_STATUS.UPDATING];

const ModalDescription = ({ instance }: { instance?: Instance }) => {
  if (instance) {
    return (
      <Dialog.Description>
        Adding a payment method will remove trial limits and will provide access to{' '}
        <TextLink target="_blank" href="https://neo4j.com/pricing/">
          full Neo4j Aura features
        </TextLink>
        .
      </Dialog.Description>
    );
  }

  return (
    <Dialog.Description>
      Adding a payment method will allow you to buy AuraDB Professional and AuraDB Business Critical instances on a
      pay-as-you-go billing model.{' '}
      <TextLink target="_blank" href="https://neo4j.com/pricing/">
        See full feature comparison
      </TextLink>
      .
    </Dialog.Description>
  );
};

const marketplaceUrl = {
  [CLOUD_PROVIDER.AWS]: 'https://aws.amazon.com/marketplace/pp/prodview-2t3o7mnw5ypee',
  [CLOUD_PROVIDER.AZURE]: 'https://azuremarketplace.microsoft.com/en-us/marketplace/apps/neo4j.neo4j_aura_professional',
  [CLOUD_PROVIDER.GCP]: 'https://console.cloud.google.com/marketplace/product/endpoints/prod.n4gcp.neo4j.io',
};

const marketplaceFriendlyNameSet = {
  [CLOUD_PROVIDER.AWS]: 'AWS',
  [CLOUD_PROVIDER.AZURE]: 'Azure',
  [CLOUD_PROVIDER.GCP]: 'GCP',
};

type MarketplaceMessageProps = {
  marketplace: CLOUD_PROVIDER;
  onClose: () => void;
};
const BaseMarketplaceMessage = ({ marketplace, onClose }: MarketplaceMessageProps) => {
  const marketplaceFriendlyName = marketplaceFriendlyNameSet[marketplace];

  return (
    <div className="mt-4 flex flex-col gap-6">
      <Typography variant="body-medium" className="flex flex-col gap-4">
        <span>You can subscribe using the Neo4j Aura pay-as-you-go plan on {marketplaceFriendlyName} marketplace.</span>
        <span>
          Click the link below and follow the instructions to subscribe. After subscribing, a new Aura organization will
          be provided. You can migrate your existing data into the new organization if you choose.{' '}
          <TextLink href="https://neo4j.com/docs/aura/cloud-providers" type="external">
            Learn more
          </TextLink>
        </span>
      </Typography>
      <div className="flex w-full items-center justify-end gap-4">
        <Button color="neutral" fill="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button as="a" href={marketplaceUrl[marketplace]} target="_blank">
          Subscribe to {marketplaceFriendlyName} marketplace
        </Button>
      </div>
    </div>
  );
};

const MarketplaceMessage = ({ marketplace, onClose }: MarketplaceMessageProps) => {
  if (marketplace === CLOUD_PROVIDER.AWS) {
    return <BaseMarketplaceMessage marketplace={marketplace} onClose={onClose} />;
  }
  if (marketplace === CLOUD_PROVIDER.AZURE) {
    return <BaseMarketplaceMessage marketplace={marketplace} onClose={onClose} />;
  }

  return <BaseMarketplaceMessage marketplace={marketplace} onClose={onClose} />;
};

type PaymentMethod = 'credit-card' | CLOUD_PROVIDER;

const BillingOptionRadio = ({
  isChecked,
  icon,
  title,
  onClick,
}: {
  isChecked: boolean;
  icon: ReactNode;
  title: string;
  onClick: () => void;
}) => {
  return (
    <div
      onClick={onClick}
      aria-checked={isChecked}
      tabIndex={0}
      role="radio"
      className={classNames(
        'border-neutral-border-weak hover:bg-neutral-hover flex cursor-pointer items-center gap-2 rounded-lg border p-3',
        {
          'border-primary-border-strong': isChecked,
          'hover:border-neutral-border-strong ': !isChecked,
        },
      )}
    >
      <div className="flex size-[30px] items-center justify-center">{icon}</div>
      <Typography variant="body-medium">{title}</Typography>
    </div>
  );
};

type BillingOption = {
  value: PaymentMethod;
  icon: ReactNode;
  title: string;
};

export const AddPaymentMethodModal = ({ onClose, project }: Props) => {
  const { instances } = useInstances({ shouldPoll: false });
  const [selectedOption, setSelectedOption] = useState<PaymentMethod>('credit-card');
  const isDarkMode = useDarkMode();

  const instance = instances
    .filter((x) => !IGNORABLE_STATUSES.includes(x.instanceStatus))
    .find((i) => isNotNullish(i.trialEndTime));

  const { data: billingAccount } = consoleApi.useGetBillingAccountForProjectQuery(project.id);
  const hasBilling = isNotNullish(billingAccount) && billingAccount.isFunded;
  const shouldShowBanner = isNotNullish(instance) && hasBilling;
  const AWSIcon = isDarkMode ? AWSDark : AWS;

  const options: BillingOption[] = useMemo(
    () => [
      { value: 'credit-card', icon: <CreditCardIconOutline className="size-6" />, title: 'Credit card' },
      {
        value: CLOUD_PROVIDER.AWS,
        icon: <img src={AWSIcon} className="size-7" draggable={false} />,
        title: 'AWS marketplace',
      },
      {
        value: CLOUD_PROVIDER.AZURE,
        icon: <img src={Azure} className="size-7" draggable={false} />,
        title: 'Azure marketplace',
      },
      {
        value: CLOUD_PROVIDER.GCP,
        icon: <img src={GCP} className="size-7" draggable={false} />,
        title: 'GCP marketplace',
      },
    ],
    [AWSIcon],
  );

  return (
    <Dialog isOpen onClose={onClose} size="large">
      <Dialog.Header>
        {isNotNullish(billingAccount) && billingAccount.isFunded ? 'Replace payment method' : 'Add payment method'}
      </Dialog.Header>
      <ModalDescription instance={instance} />
      {selectedOption === 'credit-card' && shouldShowBanner && (
        <MessageBanner project={project} instance={instance} onClose={onClose} />
      )}
      <Dialog.Content className="flex flex-col gap-6">
        <div className="grid grid-cols-4 gap-2.5" role="radiogroup">
          {options.map((option) => (
            <BillingOptionRadio
              isChecked={option.value === selectedOption}
              title={option.title}
              onClick={() => setSelectedOption(option.value)}
              icon={option.icon}
              key={option.value}
            />
          ))}
        </div>
        {selectedOption === 'credit-card' ? (
          <React.Suspense fallback={<StripeFormFallback />}>
            <StripeForm
              onCancel={onClose}
              onSuccess={onClose}
              submitButtonText={hasBilling ? 'Replace' : 'Add payment method'}
              cancelButtonText="Cancel"
            />
          </React.Suspense>
        ) : (
          <MarketplaceMessage marketplace={selectedOption} onClose={onClose} />
        )}
      </Dialog.Content>
    </Dialog>
  );
};
